import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Archive, Clock, Save, RefreshCw, Calendar, FileText,
  CheckCircle, AlertTriangle, Settings
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import api from '../../services/api';

const AutoArchiveSettingsTab = () => {
  const [settings, setSettings] = useState({
    auto_archive_enabled: false,
    archive_after_days: 30,
    archive_to_customer_files: true,
    notify_before_archive: true,
    notify_days_before: 7
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchStats();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings/auto-archive');
      if (response.data) {
        setSettings(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch auto-archive settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/settings/auto-archive/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings/auto-archive', settings);
      toast.success('Auto-archive settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async () => {
    if (!window.confirm(`This will archive all completed bookings older than ${settings.archive_after_days} days. Continue?`)) {
      return;
    }
    
    try {
      const response = await api.post('/settings/auto-archive/run');
      toast.success(`Archived ${response.data.archived_count} bookings`);
      fetchStats();
    } catch (error) {
      toast.error('Failed to run auto-archive');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-[var(--primary)]" />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Archive className="w-6 h-6 text-[var(--primary)]" />
            Auto-Archive Settings
          </h2>
          <p className="text-[var(--text-muted)] text-sm mt-1">
            Automatically archive old bookings to keep your dashboard clean
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="btn-primary">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>

      {/* Stats Card */}
      {stats && (
        <div className="card p-4 bg-[var(--background-subtle)]">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Current Status
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-[var(--background-paper)] rounded-lg">
              <p className="text-2xl font-bold text-[var(--primary)]">{stats.total_bookings || 0}</p>
              <p className="text-xs text-[var(--text-muted)]">Total Bookings</p>
            </div>
            <div className="text-center p-3 bg-[var(--background-paper)] rounded-lg">
              <p className="text-2xl font-bold text-[var(--warning)]">{stats.eligible_for_archive || 0}</p>
              <p className="text-xs text-[var(--text-muted)]">Ready to Archive</p>
            </div>
            <div className="text-center p-3 bg-[var(--background-paper)] rounded-lg">
              <p className="text-2xl font-bold text-[var(--success)]">{stats.archived_count || 0}</p>
              <p className="text-xs text-[var(--text-muted)]">Already Archived</p>
            </div>
            <div className="text-center p-3 bg-[var(--background-paper)] rounded-lg">
              <p className="text-2xl font-bold text-blue-600">{stats.in_customer_files || 0}</p>
              <p className="text-xs text-[var(--text-muted)]">In Customer Files</p>
            </div>
          </div>
        </div>
      )}

      {/* Settings Form */}
      <div className="card p-6 space-y-6">
        {/* Enable Auto-Archive */}
        <div className="flex items-center justify-between p-4 bg-[var(--background-subtle)] rounded-xl">
          <div>
            <h4 className="font-semibold">Enable Auto-Archive</h4>
            <p className="text-sm text-[var(--text-muted)]">
              Automatically archive completed bookings after a set number of days
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.auto_archive_enabled}
              onChange={(e) => setSettings({ ...settings, auto_archive_enabled: e.target.checked })}
              className="sr-only peer"
              data-testid="auto-archive-toggle"
            />
            <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#D4AF37]"></div>
          </label>
        </div>

        {/* Archive After Days */}
        <div className="p-4 bg-[var(--background-subtle)] rounded-xl">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-5 h-5 text-[var(--primary)]" />
            <h4 className="font-semibold">Archive After</h4>
          </div>
          <p className="text-sm text-[var(--text-muted)] mb-4">
            Completed bookings will be archived after this many days
          </p>
          <div className="flex items-center gap-4">
            {[30, 60, 90, 120].map(days => (
              <button
                key={days}
                onClick={() => setSettings({ ...settings, archive_after_days: days })}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  settings.archive_after_days === days
                    ? 'bg-[#D4AF37] text-black'
                    : 'bg-[var(--background-paper)] text-[var(--text-primary)] hover:bg-[var(--background-subtle)]'
                }`}
                data-testid={`archive-days-${days}`}
              >
                {days} Days
              </button>
            ))}
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="7"
                max="365"
                value={settings.archive_after_days}
                onChange={(e) => setSettings({ ...settings, archive_after_days: parseInt(e.target.value) || 30 })}
                className="w-20 px-3 py-2 bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-lg text-center"
              />
              <span className="text-[var(--text-muted)]">days</span>
            </div>
          </div>
        </div>

        {/* Archive to Customer Files */}
        <div className="flex items-center justify-between p-4 bg-[var(--background-subtle)] rounded-xl">
          <div>
            <h4 className="font-semibold flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[var(--primary)]" />
              Move to Customer Files
            </h4>
            <p className="text-sm text-[var(--text-muted)]">
              Archived bookings will be linked to the customer's file for easy reference
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.archive_to_customer_files}
              onChange={(e) => setSettings({ ...settings, archive_to_customer_files: e.target.checked })}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#D4AF37]"></div>
          </label>
        </div>

        {/* Notification Settings */}
        <div className="p-4 bg-[var(--background-subtle)] rounded-xl space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-[var(--warning)]" />
                Notify Before Archive
              </h4>
              <p className="text-sm text-[var(--text-muted)]">
                Send a reminder before bookings are archived
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.notify_before_archive}
                onChange={(e) => setSettings({ ...settings, notify_before_archive: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#D4AF37]"></div>
            </label>
          </div>
          
          {settings.notify_before_archive && (
            <div className="flex items-center gap-2 ml-7">
              <span className="text-sm text-[var(--text-muted)]">Notify</span>
              <input
                type="number"
                min="1"
                max="30"
                value={settings.notify_days_before}
                onChange={(e) => setSettings({ ...settings, notify_days_before: parseInt(e.target.value) || 7 })}
                className="w-16 px-2 py-1 bg-[var(--background-paper)] border border-[var(--border-primary)] rounded text-center"
              />
              <span className="text-sm text-[var(--text-muted)]">days before archiving</span>
            </div>
          )}
        </div>

        {/* Manual Run */}
        <div className="p-4 bg-[var(--info)]/10 border border-[var(--info)]/30 rounded-xl">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-blue-800">Run Archive Now</h4>
              <p className="text-sm text-blue-700">
                Manually archive all eligible bookings right now
              </p>
            </div>
            <Button 
              onClick={handleRunNow}
              variant="outline"
              className="border-blue-500 text-blue-700 hover:bg-blue-100"
              data-testid="run-archive-now-btn"
            >
              <Archive className="w-4 h-4 mr-2" />
              Archive Now
            </Button>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="card p-4 bg-[#D4AF37]/10 border border-[#D4AF37]/30">
        <div className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-[#D4AF37]">How Auto-Archive Works</p>
            <ul className="mt-2 space-y-1 text-[var(--text-secondary)]">
              <li>• Only <strong>completed</strong> bookings are archived</li>
              <li>• Archived bookings are moved to Customer Files for easy access</li>
              <li>• Your main Bookings list stays clean and focused on active work</li>
              <li>• Archived data is never deleted - you can always view it in Customer Files</li>
            </ul>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default AutoArchiveSettingsTab;
